import { Slot } from '@radix-ui/react-slot'
import { cn } from '@repo/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { ComponentPropsWithRef } from 'react'

const buttonVariants = cva(
  "focus-visible:miista-focus aria-invalid:border-destructive inline-flex select-none justify-center gap-1 whitespace-nowrap rounded-lg border border-solid border-transparent uppercase no-underline transition ease-out disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-2.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      // The auto variants adapt to the current theme (light or dark) whilst
      // static variants don't (e.g. for usage in sheets and dialogs).
      variant: {
        defaultStatic: 'bg-black text-white',
        defaultAuto: 'bg-(--color-foreground) text-(--color-background)',
        destructive: 'bg-destructive text-destructive-foreground',
        outline: 'border-current text-current',
        secondaryStatic: 'bg-secondary hover:backdrop-blur-xs text-black hover:border-black hover:bg-white/20',
        secondaryAuto: 'bg-secondary hover:backdrop-blur-xs hover:bg-(--color-background)/20 text-current hover:border-current',
        ghostStatic: 'text-black hover:border-black',
        ghostAuto: 'text-current hover:border-current',
        linkAuto: 'text-current underline-offset-4 hover:underline',
        navigationDefault: 'backdrop-blur-xs bg-white/20 text-black hover:border-black',
        navigationInverse: 'backdrop-blur-xs bg-black/20 text-white hover:border-white',
      },
      size: {
        default: 'px-miista-button-padding-x-default h-miista-button-height-default leading-miista-button-height-default',
        sm: 'px-miista-button-padding-x-small h-miista-button-height-small !leading-miista-button-height-small text-sm',
        lg: 'px-miista-button-padding-x-default h-miista-button-height-large leading-miista-button-height-large',
        icon: 'size-6 items-center',
        arrow: 'px-miista-button-padding-x-default h-miista-button-height-default items-center',
      },
    },
    defaultVariants: {
      variant: 'defaultStatic',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentPropsWithRef<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return <Comp data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props} />
}

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

export { Button, buttonVariants }
